/**
 * Products Feature Constants
 * All hard-coded values, configuration options, and enums for the products feature
 */

// ============================================================================
// PRODUCT STATUS OPTIONS
// ============================================================================

/**
 * Product status options
 * Use translation keys for labels: t(`products.status.${value}`)
 */
export const PRODUCT_STATUS_OPTIONS = [
  { value: "active", label: "status.active", variant: "default" },
  { value: "inactive", label: "status.inactive", variant: "secondary" },
  { value: "out_of_stock", label: "status.outOfStock", variant: "destructive" },
];

// ============================================================================
// STOCK ALERT LEVELS
// ============================================================================

/**
 * Stock alert thresholds
 */
export const STOCK_ALERT_LEVELS = {
  CRITICAL: 5,
  LOW: 10,
  MEDIUM: 20,
  HIGH: 50,
};

/**
 * Stock alert colors
 */
export const STOCK_ALERT_COLORS = {
  CRITICAL: "bg-red-100 text-red-800 border-red-200",
  LOW: "bg-orange-100 text-orange-800 border-orange-200",
  MEDIUM: "bg-yellow-100 text-yellow-800 border-yellow-200",
  HIGH: "bg-green-100 text-green-800 border-green-200",
};

// ============================================================================
// STOCK HISTORY TYPES
// ============================================================================

/**
 * Stock movement types
 * Use translation keys: t(`products.stockHistory.${value}`)
 */
export const STOCK_HISTORY_TYPES = {
  INITIAL: "initial",
  ADDITION: "addition",
  REMOVAL: "removal",
  ADJUSTMENT: "adjustment",
  RETURN: "return",
  DAMAGED: "damaged",
  SALE: "sale",
  TRANSFER: "transfer",
};

// ============================================================================
// FORM FIELD NAMES
// ============================================================================

/**
 * Product form field names
 */
export const PRODUCT_FORM_FIELDS = {
  NAME: "name",
  DESCRIPTION: "description",
  SKU: "sku",
  PRICE: "price",
  COST: "cost",
  STOCK_QUANTITY: "stockQuantity",
  MIN_STOCK_LEVEL: "minStockLevel",
  MAX_STOCK_LEVEL: "maxStockLevel",
  CATEGORY: "category",
  SUPPLIER: "supplier",
  IMAGE: "image",
  STATUS: "status",
  BARCODE: "barcode",
  WEIGHT: "weight",
  DIMENSIONS: "dimensions",
};

// ============================================================================
// VALIDATION RULES
// ============================================================================

/**
 * Product validation constants
 */
export const PRODUCT_VALIDATION = {
  NAME_MIN_LENGTH: 2,
  NAME_MAX_LENGTH: 100,
  DESCRIPTION_MAX_LENGTH: 500,
  MIN_PRICE: 0,
  MIN_STOCK: 0,
  SKU_LENGTH: 20,
};

// ============================================================================
// TOAST MESSAGE KEYS
// ============================================================================

/**
 * Toast message translation keys for product operations
 */
export const PRODUCT_TOAST_KEYS = {
  CREATING: "toast.creatingProduct",
  CREATED_SUCCESS: "toast.productCreated",
  UPDATE_SUCCESS: "toast.productUpdated",
  DELETE_SUCCESS: "toast.productDeleted",
  STOCK_UPDATED: "products.stockUpdated",
  STOCK_UPDATE_FAILED: "products.stockUpdateFailed",
  IMPORT_SUCCESS: "products.importSuccess",
  EXPORT_SUCCESS: "products.exportSuccess",
};

// ============================================================================
// ROUTE PATHS
// ============================================================================

/**
 * Product-related route paths
 */
export const PRODUCT_ROUTES = {
  LIST: "/products",
  CREATE: "/products/create",
  EDIT: "/products/edit/:id",
  DETAILS: "/products/:id",
  STOCK_MANAGEMENT: "/products/stock-management",
  STOCK_HISTORY: "/products/stock-history",
  STOCK_ANALYTICS: "/products/stock-analytics",
};

// ============================================================================
// PLACEHOLDERS
// ============================================================================

/**
 * Placeholder text for product form fields (translation keys)
 */
export const PRODUCT_PLACEHOLDERS = {
  NAME: "products.form.namePlaceholder",
  DESCRIPTION: "products.form.descriptionPlaceholder",
  SKU: "products.form.skuPlaceholder",
  PRICE: "products.form.pricePlaceholder",
  STOCK_QUANTITY: "products.form.stockQuantityPlaceholder",
};

// ============================================================================
// FILTER CONFIGURATION
// ============================================================================

/**
 * Product filter configuration
 */
export const PRODUCT_FILTER_CONFIG = {
  showCategoryFilter: true,
  showStatusFilter: true,
  showStockFilter: true,
  showPriceRangeFilter: true,
  entityName: "products",
};

// ============================================================================
// QUERY KEYS
// ============================================================================

/**
 * React Query keys for product-related queries
 */
// Query Keys
export const PRODUCT_QUERY_KEYS = {
  ALL: "products",
  LIST: "products-list",
  DETAIL: "product-detail",
  STOCK_HISTORY: "stock-history",
  LOW_STOCK: "low-stock",
  STOCK_MOVEMENTS: "stock-movements",
  STOCK_ANALYTICS: "stock-analytics",
};

// ============================================================================
// EXPORT CONFIGURATION
// ============================================================================

/**
 * Product export file configuration
 */
export const PRODUCT_EXPORT_CONFIG = {
  FILE_NAME_PREFIX: "products",
  DATE_FORMAT: "yyyy-MM-dd",
  SHEET_NAME: "Products",
};

// ============================================================================
// PAGINATION & LIMITS
// ============================================================================

/**
 * Product list pagination settings
 */
export const PRODUCT_PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
};
